<template>
  <header class="w-full bg-white">
    <div
      class="flex w-full items-center justify-between px-4 pb-2 pt-4 sm:px-5 md:px-24"
    >
      <!-- LEFT: logo lockup -->
      <div
        class="flex cursor-pointer items-center gap-3"
        role="link"
        aria-label="Go to homepage"
        @click="onLogoClick"
      >
        <div class="flex flex-col items-center gap-[2px]">
          <img
            src="/images/logo_color.svg"
            alt="Upfest logo"
            width="72"
            height="72"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            class="h-9 w-auto"
          />
          <span
            class="text-[13px] font-extrabold uppercase leading-none tracking-[0.08em] text-[#412c71]"
            >Upfest</span
          >
        </div>
      </div>

      <!-- CENTER: nav toggles -->
      <nav class="hidden items-center gap-10 md:flex">
        <NavToggle
          :label="$t('nav.event')"
          value="event"
          :active="!isHowItWorks && activeMode === 'event'"
          @select="onSelectNav"
        />
        <NavToggle
          :label="$t('nav.establishment')"
          value="establishment"
          :active="!isHowItWorks && activeMode === 'establishment'"
          @select="onSelectNav"
        />

        <NavToggle
          :label="$t('nav.howItWorks')"
          value="how-it-works"
          :active="isHowItWorks"
          @select="onSelectNav"
        />
      </nav>

      <!-- RIGHT: actions -->
      <div class="flex items-center gap-3">
        <GradientButton
          :text="$t('nav.login')"
          width="128px"
          height="40px"
          :on-click="() => emit('login')"
          class="hidden md:flex"
        />
        <HamburgerButton class="md:hidden" @click="toggleMobileMenu" />
      </div>
    </div>
  </header>

  <!-- Mobile menu overlay container -->
  <div v-if="showMobileMenu" class="fixed inset-0 z-50 flex">
    <!-- Backdrop with fade transition -->
    <transition name="fade">
      <div
        v-if="showMobileMenu"
        class="absolute inset-0 bg-black/50"
        @click="showMobileMenu = false"
      />
    </transition>

    <!-- Aside menu with slide transition -->
    <transition name="slide">
      <aside
        v-if="showMobileMenu"
        class="relative z-50 flex h-[100svh] w-full flex-col gap-5 overflow-auto bg-white px-5 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-5 shadow-2xl"
      >
        <div class="flex items-center justify-end">
          <CloseButton @close="showMobileMenu = false" />
        </div>

        <nav class="flex flex-col items-stretch gap-2 text-center">
          <button
            :class="[
              'w-full rounded-xl border px-4 py-3 text-center text-[15px] font-semibold',
              !isHowItWorks && activeMode === 'event'
                ? 'border-pink-200 bg-pink-50 text-pink-700'
                : 'border-gray-100 text-[#2e234b] hover:bg-gray-50',
            ]"
            @click="
              () => {
                setMode('event');
                showMobileMenu = false;
              }
            "
          >
            {{ $t("nav.event") }}
          </button>
          <button
            :class="[
              'w-full rounded-xl border px-4 py-3 text-center text-[15px] font-semibold',
              !isHowItWorks && activeMode === 'establishment'
                ? 'border-pink-200 bg-pink-50 text-pink-700'
                : 'border-gray-100 text-[#2e234b] hover:bg-gray-50',
            ]"
            @click="
              () => {
                setMode('establishment');
                showMobileMenu = false;
              }
            "
          >
            {{ $t("nav.establishment") }}
          </button>
          <button
            :class="[
              'w-full rounded-xl border px-4 py-3 text-center text-[15px] font-semibold',
              isHowItWorks
                ? 'border-pink-200 bg-pink-50 text-pink-700'
                : 'border-gray-100 text-[#2e234b] hover:bg-gray-50',
            ]"
            @click="
              () => {
                navigateTo(localePath('/how-it-works'));
                showMobileMenu = false;
              }
            "
          >
            {{ $t("nav.howItWorks") }}
          </button>
        </nav>

        <div class="mt-auto pt-3">
          <button
            class="w-full rounded-xl bg-[#2e234b] px-4 py-3 text-center text-sm font-semibold text-white hover:bg-[#251b3d]"
            @click="
              () => {
                emit('login');
                showMobileMenu = false;
              }
            "
          >
            {{ $t("nav.login") }}
          </button>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import NavToggle from "./NavToggle.vue";
import HamburgerButton from "./HamburgerButton.vue";
import GradientButton from "../buttons/GradientButton.vue";
import CloseButton from "../buttons/CloseButton.vue";

type Mode = "event" | "establishment";

const props = defineProps<{
  mode: Mode;
}>();

const emit = defineEmits<{
  (e: "update:mode", v: Mode): void;
  (e: "login" | "searchClick" | "menuClick" | "howItWorksClick"): void;
}>();

const route = useRoute();
const router = useRouter();
const showMobileMenu = ref(false);

const localePath = useLocalePath();

const isHowItWorks = computed(() => {
  const howPath = localePath("/how-it-works");
  return route.path === howPath || route.path.startsWith(howPath);
});

const activeMode = computed<Mode>(() => {
  const queryMode = route.query.mode;
  if (queryMode === "event" || queryMode === "establishment") {
    return queryMode;
  }

  return props.mode;
});

function onSelectNav(v: string) {
  if (v === "how-it-works") {
    emit("howItWorksClick");
    navigateTo(localePath("/how-it-works"));
    return;
  }

  if (v === "event" || v === "establishment") {
    setMode(v as Mode);
  }
}

function setMode(v: Mode) {
  if (route.path === localePath("/")) {
    emit("update:mode", v);
  } else if (
    route.path === localePath("/events") ||
    route.path === localePath("/venues")
  ) {
    router.push(localePath(v === "event" ? "/events" : "/venues"));
  } else {
    router.push({
      path: localePath("/"),
      query: { mode: v },
    });
  }
}

function toggleMobileMenu() {
  emit("menuClick");
  showMobileMenu.value = !showMobileMenu.value;
}

function onLogoClick() {
  router.push(localePath("/"));
}
</script>

<!-- styles removed: replaced by Tailwind utilities in template -->

<style scoped>
/* Backdrop fade */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 220ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

/* Slide-in menu from the right */
.slide-enter-active {
  transition:
    transform 260ms cubic-bezier(0.2, 0.9, 0.2, 1) 80ms,
    opacity 260ms ease 80ms;
}

.slide-leave-active {
  transition:
    transform 180ms ease,
    opacity 180ms ease;
}

.slide-enter-from {
  transform: translateX(18px);
  opacity: 0;
}

.slide-enter-to {
  transform: translateX(0);
  opacity: 1;
}

.slide-leave-from {
  transform: translateX(0);
  opacity: 1;
}

.slide-leave-to {
  transform: translateX(12px);
  opacity: 0;
}
</style>
